import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  ShieldCheck,
  Moon,
  Sun,
  LogOut,
  User,
  KeyRound,
  FileCheck2,
  ExternalLink,
} from "lucide-react";

export const Navbar = () => {
  const { currentUser, userProfile, logout, theme, toggleTheme } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (e) {
      console.error("Logout error:", e);
    }
  };

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border/40 bg-background/90 backdrop-blur-md transition-colors">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-2 min-w-0">
        <Link
          to="/"
          data-testid="nav-brand-link"
          className="flex items-center gap-2 sm:gap-3 group min-w-0 flex-1 overflow-hidden"
        >
          <div className="w-9 h-9 shrink-0 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-500 shadow-sm group-hover:border-emerald-500/60 transition-all">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
          </div>
          <div className="min-w-0 overflow-hidden">
            <div className="font-bold text-sm sm:text-base tracking-tight flex items-center gap-1.5 text-foreground min-w-0">
              <span className="truncate">Kian Technologies</span>
              <span className="hidden sm:inline-flex shrink-0 text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                Bhilai
              </span>
            </div>
            <p className="hidden sm:block text-[11px] text-muted-foreground font-mono truncate">
              15 Days Cyber Security Masterclass
            </p>
          </div>
        </Link>

        <div className="flex items-center space-x-3">
          {!currentUser && (
            <nav className="hidden lg:flex items-center gap-4 mr-2">
              <Link to="/about" className="text-xs text-muted-foreground hover:text-foreground transition-colors">About</Link>
              <Link to="/faculty" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Faculty</Link>
              <Link to="/chief-guest" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Chief Guest</Link>
              <Link to="/contact" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Contact</Link>
            </nav>
          )}

          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-btn"
            className="p-2 rounded-lg border border-border bg-card hover:bg-muted text-foreground transition-colors"
            title={`Switch to ${theme === "dark" ? "Light" : "Dark"} mode`}
            aria-label="Toggle theme"
          >
            {theme === "dark" ? (
              <Sun className="w-4 h-4 text-amber-400" />
            ) : (
              <Moon className="w-4 h-4 text-slate-700" />
            )}
          </button>

          {currentUser ? (
            <div className="flex items-center space-x-3">
              {userProfile?.role === "admin" ? (
                <Link
                  to="/admin"
                  data-testid="nav-admin-dashboard-link"
                  className="text-xs font-mono font-medium px-3 py-1.5 rounded-md bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20 transition-colors"
                >
                  Admin Console
                </Link>
              ) : (
                <Link
                  to="/student/dashboard"
                  data-testid="nav-student-dashboard-link"
                  className="text-xs font-mono font-medium px-3 py-1.5 rounded-md bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/20 transition-colors"
                >
                  Student Portal
                </Link>
              )}

              <button
                onClick={handleLogout}
                data-testid="nav-logout-btn"
                className="flex items-center space-x-1.5 text-xs font-medium px-3 py-1.5 rounded-md border border-border hover:bg-destructive/10 hover:text-destructive text-muted-foreground transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          ) : (
            <div className="flex items-center space-x-2 sm:space-x-3">
              <Link
                to="/register"
                data-testid="nav-register-btn"
                className="text-xs sm:text-sm font-medium px-3 sm:px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white shadow-sm transition-all flex items-center gap-1.5 font-mono"
              >
                <FileCheck2 className="w-4 h-4" />
                <span>Apply Now</span>
              </Link>
              <Link
                to="/login"
                data-testid="nav-login-btn"
                className="text-xs sm:text-sm font-medium px-3 sm:px-4 py-2 rounded-lg border border-border bg-card hover:bg-muted text-foreground transition-all flex items-center gap-1.5 font-mono"
              >
                <KeyRound className="w-3.5 h-3.5 text-emerald-500" />
                <span>Login</span>
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="w-full border-t border-border/40 bg-card/50 py-8 text-xs text-muted-foreground font-mono">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center space-x-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
          <span>Kian Technologies, Bhilai, Chhattisgarh</span>
          <span>•</span>
          <span className="text-foreground font-medium">15 Days Tuition-Free Masterclass</span>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
          <Link to="/about" className="hover:text-emerald-400 transition-colors">About</Link>
          <Link to="/faculty" className="hover:text-emerald-400 transition-colors">Faculty</Link>
          <Link to="/chief-guest" className="hover:text-emerald-400 transition-colors">Chief Guest</Link>
          <Link to="/contact" className="hover:text-emerald-400 transition-colors">Contact</Link>
          <Link to="/privacy-policy" className="hover:text-emerald-400 transition-colors">Privacy</Link>
          <Link to="/terms" className="hover:text-emerald-400 transition-colors">Terms</Link>
          <a
            href="https://kiantechnologies.in"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-emerald-400 flex items-center gap-1 transition-colors"
          >
            kiantechnologies.in <ExternalLink className="w-3 h-3" />
          </a>
          <a
            href="https://edu.kiantechnologies.in"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-emerald-400 flex items-center gap-1 transition-colors"
          >
            edu.kiantechnologies.in <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </footer>
  );
};
